import { client } from "@/hera-api/client/client.gen";

const SECURITY = [{ scheme: "bearer", type: "http" }] as const;

export interface StrGovernmentContact {
  heading: string;
  name: string;
  title: string;
  phone: string;
  email: string;
  website: string;
  booking_url: string;
}

export interface StrGovernmentLicenseSummary {
  license_id: string;
  status: string;
  display_id: string | null;
  registrant_name: string | null;
  created_at: string | null;
}

export interface StrGovernmentProperty {
  id: string;
  listing_count: number;
  platforms: string[];
  title: string | null;
  address: string | null;
  city: string | null;
  mapped: boolean;
  compliance: "compliant" | "non_compliant" | "unmapped" | string;
  pin_color: string;
  status_label: string;
  address_unverified?: boolean;
  regions: string[];
  latitude: number | null;
  longitude: number | null;
  property_id: string | null;
  licenses: StrGovernmentLicenseSummary[];
}

export interface StrGovernmentPage {
  configured: boolean;
  contact: StrGovernmentContact | null;
  items: StrGovernmentProperty[];
  total: number;
  page: number;
  page_size: number;
  show_regions?: boolean;
  jurisdiction_id?: string | null;
  jurisdiction_name?: string | null;
}

export interface StrGovernmentPins {
  configured: boolean;
  items: StrGovernmentPin[];
}

export interface StrGovernmentPin {
  id: string;
  latitude: number;
  longitude: number;
  title: string | null;
  address?: string | null;
  mapped: boolean;
  compliance: string;
  pin_color: string;
  status_label: string;
  license_count: number;
  platforms?: string[];
  hero_image_url?: string | null;
  address_unverified?: boolean;
}

export interface StrPhoto {
  url: string;
  photo_index: number;
  room_category: string | null;
  image_id: string | null;
}

export interface StrListingDetail {
  id: string;
  platform: string;
  title: string | null;
  bedrooms: number | null;
  beds: number | null;
  bathrooms: number | null;
  guests: number | null;
  nightly_price: number | null;
  active: boolean;
  url: string | null;
  first_seen_on?: string | null;
  host_name?: string | null;
  host_profile_url?: string | null;
  photos: StrPhoto[];
}

export interface StrGovernmentDetail {
  configured: boolean;
  group: StrGovernmentProperty;
  featured_listing_id: string | null;
  listings: StrListingDetail[];
}

function requireData<T>(
  result: { error?: unknown; data?: T },
  fallback: string,
): T {
  if (result.error) {
    const error = result.error as { detail?: string; message?: string };
    throw new Error(error.detail ?? error.message ?? fallback);
  }
  if (result.data === undefined) throw new Error(fallback);
  return result.data;
}

export async function getGovernmentStrProperties(options: {
  page: number;
  pageSize?: number;
  regionIds?: string[];
  includeUnassignedRegions?: boolean;
  search?: string;
  sort?: string;
  direction?: "asc" | "desc";
  filters?: string;
}): Promise<StrGovernmentPage> {
  const result = await client.get<StrGovernmentPage>({
    url: "/government/str-properties",
    query: {
      page: options.page,
      page_size: options.pageSize ?? 50,
      ...(options.regionIds?.length ? { region_ids: options.regionIds } : {}),
      ...(options.includeUnassignedRegions
        ? { include_unassigned_regions: true }
        : {}),
      ...(options.search ? { search: options.search } : {}),
      ...(options.sort ? { sort: options.sort } : {}),
      ...(options.direction ? { direction: options.direction } : {}),
      ...(options.filters ? { filters: options.filters } : {}),
    },
    parseAs: "json",
    security: [...SECURITY],
  });
  return requireData(result, "Failed to load short-term rentals");
}

type GovernmentStrScope = {
  regionIds?: string[];
  includeUnassignedRegions?: boolean;
  search?: string;
  filters?: string;
};

function scopeQuery(options: GovernmentStrScope) {
  return {
    ...(options.regionIds?.length ? { region_ids: options.regionIds } : {}),
    ...(options.includeUnassignedRegions
      ? { include_unassigned_regions: true }
      : {}),
    ...(options.search ? { search: options.search } : {}),
    ...(options.filters ? { filters: options.filters } : {}),
  };
}

export async function getGovernmentStrPins(
  options: GovernmentStrScope = {},
): Promise<StrGovernmentPin[]> {
  const result = await client.get<StrGovernmentPins>({
    url: "/government/str-properties/map-pins",
    query: scopeQuery(options),
    parseAs: "json",
    security: [...SECURITY],
  });
  return requireData(result, "Failed to load short-term rental pins").items;
}

export async function getGovernmentStrPropertyFilterValues(
  options: GovernmentStrScope & { column: string; q?: string },
): Promise<string[]> {
  const result = await client.get<{ 200: { values: string[] } }>({
    url: "/government/str-properties/filter-values",
    query: {
      column: options.column,
      ...scopeQuery(options),
      ...(options.q ? { q: options.q } : {}),
    },
    parseAs: "json",
    security: [...SECURITY],
  });
  const data = requireData<{ values: string[] }>(
    result,
    "Failed to load short-term rental filters",
  );
  return data.values;
}

export async function getAllGovernmentStrProperties(
  options: Omit<
    Parameters<typeof getGovernmentStrProperties>[0],
    "page" | "pageSize"
  >,
): Promise<StrGovernmentProperty[]> {
  const rows: StrGovernmentProperty[] = [];
  let page = 1;
  for (;;) {
    const result = await getGovernmentStrProperties({
      ...options,
      page,
      pageSize: 200,
    });
    rows.push(...result.items);
    if (rows.length >= result.total || result.items.length === 0) return rows;
    page += 1;
  }
}

export async function getGovernmentStrProperty(
  groupId: string,
): Promise<StrGovernmentDetail> {
  const result = await client.get<StrGovernmentDetail>({
    url: `/government/str-properties/${groupId}`,
    parseAs: "json",
    security: [...SECURITY],
  });
  return requireData(result, "Failed to load short-term rental");
}
